// Bridges view drawables to the Part B sorter: fragments for static primitives, city seeds and cars; a static graph
// cached per (rotation, composition version, retained chunk set, atlas); per-frame car edges and Kahn order; trace
// precedence metadata (fragment/proxy ids, crossing membership, required and broken edges).
import type { RuntimeAtlas } from './atlasContract.js';
import type { Alignment, SpriteId } from './contracts.js';
import { proxyScreenBounds, SUPPORT_RANK, type Bounds, type CrossingStrata, type Fragment, type Proxy, type XY } from './precedence.js';
import { buildStaticGraph, sortFrame, type FrameSortResult, type StaticGraph } from './precedenceGraph.js';
import { tileToScreen, type Rotation } from './projection.js';
import { carProxy, placeProxy, tileProxy } from './proxies.js';
import type { AtlasSpriteId, DrawablePrecedence } from './scene.js';
import { corridorSupport, primitiveFrame, type StaticPrimitive } from './staticWorld.js';

export type OrderAtlas = Pick<RuntimeAtlas, 'level' | 'sprites' | 'components'>;
interface FrameLike { rect: readonly number[]; anchor: readonly number[] }

export const atlasHeadingCount = (atlas: Pick<RuntimeAtlas, 'sprites'>): number => atlas.sprites.track.rotations;
const spriteEntry = (atlas: OrderAtlas, id: AtlasSpriteId): { rotations: number; frames: readonly FrameLike[] } => {
  const entry = (atlas.sprites as Record<string, { rotations: number; frames: readonly FrameLike[] } | undefined>)[id] ?? atlas.components?.[id];
  if (!entry) throw new Error(`atlas has no sprite ${id}`);
  return entry;
};
/** Unit-scale screen bounds of a sprite frame anchored at a tile and height; clip halves split at the anchor row. */
export function spriteUnitBounds(frame: FrameLike, level: number, tile: XY, height: number, rotation: Rotation, clip?: 'back' | 'front'): [number, number, number, number] {
  const [ax, ay] = tileToScreen(tile as [number, number], { center: [0, 0], rotation, zoom: 1 }, height);
  const x0 = ax - frame.anchor[0]! / level, y0 = ay - frame.anchor[1]! / level, x1 = x0 + frame.rect[2]! / level, y1 = y0 + frame.rect[3]! / level;
  return clip === 'back' ? [x0, y0, x1, Math.max(y0, ay)] : clip === 'front' ? [x0, Math.min(ay, y1), x1, y1] : [x0, y0, x1, y1];
}
const union = (a: Bounds, b: Bounds): [number, number, number, number] => [Math.min(a[0], b[0]), Math.min(a[1], b[1]), Math.max(a[2], b[2]), Math.max(a[3], b[3])];

export function staticFragment(primitive: StaticPrimitive, rotation: Rotation, atlas: OrderAtlas): Fragment {
  const local = primitive.proxy(rotation), proxy = placeProxy(`proxy:${primitive.stableId}`, local, primitive.tile, primitive.heading, primitive.height);
  const spriteId = primitive.sprite(rotation), entry = spriteEntry(atlas, spriteId), frame = entry.frames[primitiveFrame(primitive, rotation, entry.rotations, atlasHeadingCount(atlas))]!;
  return {
    id: primitive.stableId, proxy, bounds: union(spriteUnitBounds(frame, atlas.level, primitive.tile, primitive.height, rotation, primitive.clip), proxyScreenBounds(proxy, rotation)),
    corridorId: primitive.corridorId, range: primitive.range, role: local.role, anchor: primitive.tile, height: primitive.sortHeight ?? primitive.height,
    dynamic: false, visible: true, crossingIds: primitive.crossingIds, overlapRegionId: primitive.overlapRegionId, support: primitive.support ?? null, ...(primitive.bedRank === undefined ? {} : { bedRank: primitive.bedRank }),
  };
}

export interface CitySeedLike { stableId: string; spriteId: SpriteId; tile: readonly [number, number] }
/** Buildings are tile-aligned prisms of their display height; trees are canopy-inclusive prisms. */
export function cityFragment(seed: CitySeedLike, rotation: Rotation, atlas: OrderAtlas): Fragment {
  const sprite = atlas.sprites[seed.spriteId], proxy: Proxy = { ...tileProxy(`proxy:${seed.stableId}`, seed.tile, sprite.heightUnits), id: `proxy:${seed.stableId}` };
  return {
    id: seed.stableId, proxy, bounds: union(spriteUnitBounds(sprite.frames[0]!, atlas.level, seed.tile, 0, rotation), proxyScreenBounds(proxy, rotation)),
    corridorId: null, range: null, role: 'solid', anchor: seed.tile, height: 0, dynamic: false, visible: true, crossingIds: [], overlapRegionId: null, scenery: true,
  };
}

export interface CarNodeInput {
  stableId: string; spriteId: AtlasSpriteId; frameIndex: number; tile: XY; heading: number; railHeight: number;
  pitchMeters: number; widthMeters: number; corridorId: string | null; c: number | null; alignment: Alignment;
}
/** One distinct car pose: coincident cars (same sprite frame, tile, heading, height, span) share a fragment. */
export interface CarPoseGroup { readonly input: CarNodeInput; readonly members: string[]; crossingIds: readonly string[] }
const samePose = (a: CarNodeInput, b: CarNodeInput): boolean =>
  a.spriteId === b.spriteId && a.frameIndex === b.frameIndex && a.tile[0] === b.tile[0] && a.tile[1] === b.tile[1] && a.heading === b.heading
  && a.railHeight === b.railHeight && a.pitchMeters === b.pitchMeters && a.widthMeters === b.widthMeters && a.corridorId === b.corridorId && a.c === b.c && a.alignment === b.alignment;
/** Collects the frame's on-screen cars into distinct poses so fragment construction and crossing tests run once per pose. */
export class CarPosePool {
  private readonly byTile = new Map<number, CarPoseGroup[]>();
  readonly groups: CarPoseGroup[] = [];
  add(input: CarNodeInput): CarPoseGroup {
    const bucket = this.byTile.get(input.tile[0]), found = bucket?.find((group) => samePose(group.input, input));
    if (found) { found.members.push(input.stableId); return found; }
    const group: CarPoseGroup = { input, members: [input.stableId], crossingIds: [] };
    this.groups.push(group);
    if (bucket) bucket.push(group); else this.byTile.set(input.tile[0], [group]);
    return group;
  }
}
export function carFragment(group: CarPoseGroup, rotation: Rotation, atlas: OrderAtlas): Fragment {
  const car = group.input, proxy = carProxy(`proxy:${car.stableId}`, car.tile, car.heading, car.railHeight, car.pitchMeters, car.widthMeters, car.alignment);
  const frame = spriteEntry(atlas, car.spriteId).frames[car.frameIndex]!;
  return {
    id: car.stableId, proxy, bounds: union(spriteUnitBounds(frame, atlas.level, car.tile, car.railHeight, rotation), proxyScreenBounds(proxy, rotation)),
    corridorId: car.corridorId, range: car.c === null ? null : [car.c - car.pitchMeters / 2, car.c + car.pitchMeters / 2], role: 'car', anchor: car.tile, height: car.railHeight,
    dynamic: true, visible: true, crossingIds: group.crossingIds, overlapRegionId: null, support: corridorSupport(car.corridorId, SUPPORT_RANK.rider), ...(group.members.length > 1 ? { members: group.members } : {}),
  };
}

const EMPTY: readonly string[] = Object.freeze([]);
const NO_PRECEDENCE: DrawablePrecedence = Object.freeze({ crossingIds: EMPTY, requiredAfter: EMPTY, brokenAfter: EMPTY });
export interface OrderedScene {
  order: readonly string[]; stats: FrameSortResult['stats']; result: FrameSortResult;
  /** Shared trace metadata for `id`; `crossingIds` is the fragment's crossing membership. */
  precedence(id: string, crossingIds: readonly string[]): DrawablePrecedence;
}

/** Caches one static graph; a different key (rotation, composition, chunk set, atlas, static world) rebuilds it. */
export class SceneOrderer {
  private cached: { key: string; graph: StaticGraph } | undefined;
  builds = 0;
  graph(key: string, rotation: Rotation, strata: readonly CrossingStrata[], fragments: () => readonly Fragment[]): StaticGraph {
    if (this.cached?.key !== key) {
      this.cached = { key, graph: buildStaticGraph(fragments(), { rotation, crossings: strata, key }) };
      this.builds += 1;
    }
    return this.cached.graph;
  }
  invalidate(): void { this.cached = undefined; }
  order(graph: StaticGraph, dynamic: readonly Fragment[], visibleStatic: ReadonlySet<string>): OrderedScene {
    const result = sortFrame(graph, dynamic, { staticVisible: (index) => visibleStatic.has(graph.ids[index]!), collectRequired: true });
    const required = new Map<string, string[]>(), broken = new Map<string, string[]>();
    for (const edge of result.requiredEdges) { const list = required.get(edge.after); if (list) list.push(`${edge.before}>${edge.reason}`); else required.set(edge.after, [`${edge.before}>${edge.reason}`]); }
    for (const edge of result.brokenEdges) { const list = broken.get(edge.after); if (list) list.push(`${edge.before}>${edge.reason}`); else broken.set(edge.after, [`${edge.before}>${edge.reason}`]); }
    for (const list of required.values()) list.sort();
    for (const list of broken.values()) list.sort();
    const shared = new Map<readonly string[], DrawablePrecedence>();
    return {
      order: result.order, stats: result.stats, result,
      precedence(id, crossingIds) {
        const requiredAfter = required.get(id), brokenAfter = broken.get(id);
        if (requiredAfter || brokenAfter) return { crossingIds, requiredAfter: requiredAfter ?? EMPTY, brokenAfter: brokenAfter ?? EMPTY };
        if (!crossingIds.length) return NO_PRECEDENCE;
        let value = shared.get(crossingIds);
        if (!value) { value = { crossingIds, requiredAfter: EMPTY, brokenAfter: EMPTY }; shared.set(crossingIds, value); }
        return value;
      },
    };
  }
}
